/* global GM_STATE */
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { fetchData, postData } from 'helpers/fetch'
import { addClass, select, selectAll, removeClass } from 'helpers/dom'
import { hasOwnProperties } from 'helpers/utils'
import { formatMoney } from 'libs/shopify'

const selectors = {
  CART_COUNT_SELECTOR: '#CartCount',
  MINI_CART_CONTENT_CLASS: '.js-mini-cart-content',
  MAX_CART_ITEM_QUANTITY: 1000000,
  HAS_ITEM_CLASS: 'has-items',
  CART_TOTAL_SELECTOR: '.js-cart-total'
}

const cartError = async (response) => {
  const error = await response.json()

  globalEmitter.emit(emitterProps.notice.global, {
    type: 'error',
    content: `${response.status}: ${error.description}`
  })
  throw new Error(`${response.status}: ${error.description}`)
}

const getNativeCart = async () => {
  const cart = await fetchData('/cart.js', cartError)

  return cart
}

const getCart = async () => {
  const cart = await fetchData('/cart?view=ajax', cartError)

  return cart
}

const getLineItem = (id, properties, quantity) => {
  const data = {
    items: [
      {
        id,
        properties,
        quantity
      }
    ]
  }

  return data
}

const addCartItem = async (id, properties = {}, quantity = 1) => {
  const lineItem = getLineItem(id, properties, quantity)

  return postData('/cart/add.js', lineItem, 'json', cartError).then((item) => {
    return item
  })
}

const addMultipleCartItems = async (items) => {
  return postData('/cart/add.js', items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  })
}

const updateMultipleCartItems = async (items) => {
  return postData('/cart/update.js', items, 'json', async (response) => {
    const { description } = await response.json()

    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: description
    })
  }).then((items) => {
    return items
  }).catch((error) => cartError(error))
}

const updateCartItem = async (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.change, cart.item_count)

    return cart
  })
}

const getBundleItem = async (item) => {
  const newCart = await getCart()

  return getItemsByKey(newCart.items, item, 'timestamp')
}

const updateCartBundle = async (item, quantity) => {
  if (!quantity) {
    await removeBundleItem(item)

    return
  }

  const bundleItem = await getBundleItem(item)

  if (!bundleItem?.length) return

  const newUpdates = {}

  bundleItem.forEach(itemCart => {
    newUpdates[itemCart.key] = quantity
  })

  const body = {
    updates: newUpdates
  }

  await updateMultipleCartItems(body)
}

const changeQuantity = async (id, quantity) => {
  return postData('/cart/change.js', {
    id,
    quantity
  }, 'json', cartError).then((cart) => {
    return cart
  })
}

const removeCartItem = async (id) => {
  return postData('/cart/change.js', {
    id,
    quantity: 0
  }, 'json', cartError).then((cart) => {
    if (cart?.count) {
      globalEmitter.emit(emitterProps.cart.change, cart.item_count)
    }

    return cart
  })
}

const clearCart = async () => {
  return postData('/cart/clear.js', {}, 'json', cartError).then((cart) => {
    globalEmitter.emit(emitterProps.cart.clear, cart)

    return cart
  })
}

const reRenderCartCount = (count, totalPrice) => {
  const els = selectAll(selectors.CART_COUNT_SELECTOR, document.body)
  const cartTotalEls = selectAll(selectors.CART_TOTAL_SELECTOR)

  if (els) {
    if (count === 0) {
      els.forEach((el) => addClass('hidden', el))
      removeClass(selectors.HAS_ITEM_CLASS, document.body)
    } else {
      els.forEach((el) => {
        el.hasAttribute('data-type') ? el.innerText = `(${count})` : el.innerText = count
        removeClass('hidden', el)
        addClass(selectors.HAS_ITEM_CLASS, document.body)
        addClass('inline-block', el)
      })
    }
  }

  if (cartTotalEls) {
    cartTotalEls.forEach(el => {
      el.innerText = `${formatMoney(totalPrice)}`
    })
  }
}

const updateCart = async () => {
  const newCart = await getCart()

  if (newCart) {
    reRenderCartCount(newCart.item_count, newCart.total_price)
  }

  return newCart
}

const forceReloadItem = async (item) => {
  const currentQty = item.quantity
  const nextQty = item.quantity + 1

  await changeQuantity(item.key, nextQty)

  const cart = await changeQuantity(item.key, currentQty)

  return cart
}

const openMiniCart = () => {
  const el = select('cart-slideout modal-dialog', document.body)

  if (el) {
    el.show()

    const contentEl = select(selectors.MINI_CART_CONTENT_CLASS, el)

    if (contentEl) {
      contentEl.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}

const getCartProperties = (items) => {
  const arrs = []

  if (items && Object.keys(items).length > 0) {
    for (const [key, value] of Object.entries(items)) {
      arrs.push({
        name: key,
        key: value
      })
    }
  }

  return arrs
}

const getDisableChangeQuantity = (properties) => {
  return properties && (properties.some(item => item.name === 'Bundle') || properties?._disable_quantity === 'true')
}

const getItemsByKey = (items, item, key) => items.filter((cartItem) => cartItem.properties && hasOwnProperties(key, cartItem.properties) && cartItem.properties.timestamp === item.properties.timestamp)

const getMaxQuantity = (cartItem) => {
  return cartItem.inventory_management === 'shopify' && cartItem.inventory_policy === 'deny' && cartItem.inventory_quantity > 0
    ? cartItem.inventory_quantity
    : selectors.MAX_CART_ITEM_QUANTITY
}

const removeBundleItem = async (item) => {
  const bundleItem = await getBundleItem(item)

  if (!bundleItem?.length) return

  const newUpdates = {}

  for (const itemCart of bundleItem) {
    newUpdates[itemCart.key] = 0
  }

  const body = {
    updates: newUpdates
  }

  await updateMultipleCartItems(body)
}

const getMaxQuantityByVariant = (cartItems, variant) => {
  if (!variant.inventory_management || variant.inventory_policy === 'continue') return selectors.MAX_CART_ITEM_QUANTITY

  const currentItem = cartItems.find(item => item.id === variant.id)

  return currentItem
    ? variant.inventory_quantity - currentItem.quantity
    : variant.inventory_quantity
}

export {
  getNativeCart,
  getCart,
  addCartItem,
  updateCartItem,
  updateCartBundle,
  removeCartItem,
  clearCart,
  updateCart,
  openMiniCart,
  getCartProperties,
  addMultipleCartItems,
  getDisableChangeQuantity,
  forceReloadItem,
  updateMultipleCartItems,
  getItemsByKey,
  getMaxQuantity,
  removeBundleItem,
  reRenderCartCount,
  getMaxQuantityByVariant
}
